import React, { useCallback, useMemo } from "react";
import CustomTable, { Column } from "../../../components/CustomTable";
import { ArrowPathIcon, ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { getUserIdTokenForIonTime, removeCookieData, setCookieData } from "../../../helper/api";
import { useReportTimesheetContext } from "../../../context/ReportTimsheetContex";
import { timesheetListResponse } from "../../../model/timesheet";
import moment from "moment";
import { useLoading } from "../../../context/LoadingContext";
import { useAuth } from "../../../context/LoginContext";

export interface Data {
  id: number;
  projectName: string;
  timesheetPeriod: string;
  tsApprover: string;
  status: string;
}

const TimesheetPage: React.FC = () => {
  const navigate = useNavigate();
  const { isLoading } = useLoading();
  const { user } = useAuth();
  const getUserId = getUserIdTokenForIonTime();
  const { timesheetList, getTimesheetStatusList } = useReportTimesheetContext();
  const [statusValueData, setStateValueData] = React.useState<string>("1");
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pagePercount] = React.useState(10);

  const columns: Column<timesheetListResponse>[] = [
    { header: "Project Name", key: "projects", enableLink: false, path: "" },
    {
      header: "Timesheet Period",
      key: "start_date",
      enableLink: true,
      path: "/mytimesheetreport",
    },
    { header: "TS Approver", key: "approvers", enableLink: false, path: "" },
    { header: "Status", key: "status", enableLink: false, path: "" },
  ];

  const getCall = useCallback(
    async (statusVal: number, pageNo?: number | null) => {
      if (getUserId) {
        const payload = {
          user_id: Number(getUserId),
          status: [statusVal],
          page: pageNo ?? currentPage,
          page_per_count: pagePercount,
        };
        await getTimesheetStatusList(payload);
      }
    },
    [currentPage, pagePercount, getUserId, getTimesheetStatusList],
  );

  React.useEffect(() => {
    getCall(Number(statusValueData), null);
  }, []);

  const mytimesheetList = useMemo(() => {
    return timesheetList?.data.map((item) => ({
      ...item,
      start_date:
        moment(item.start_date).format("D MMM YYYY") + " - " + moment(item.end_date).format("D MMM YYYY"),
      approvers: item.approvers.join(", "),
      projects: item.projects.join(", "),
    }));
  }, [timesheetList]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  const handleNavigation = (path: string | undefined, states: number | string) => {
    setCookieData(
      "timesheetsReport",
      JSON.stringify({
        id: states,
        memberID: getUserId,
        role: null,
        employeeNameData: user.ldap_uid,
        employeeIDData: user.employee_id,
      }),
    );
    navigate(path ?? "", { state: { id: states } }); // Navigate to the specified path with state
  };

  const refreshTable = () => {
    getCall(1, 1);
    setStateValueData("");
  };

  const totalPages = Math.ceil(Number(timesheetList?.record_total_count) / pagePercount);

  const startRecord = (currentPage - 1) * pagePercount + 1;
  const endRecord = Math.min(startRecord + pagePercount - 1, Number(timesheetList?.record_total_count));

  const handlePrePage = () => {
    if (currentPage > 1) {
      getCall(Number(statusValueData), currentPage - 1);
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      getCall(Number(statusValueData), currentPage + 1);
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  console.log("", timesheetList?.missing_timesheet);

  return (
    <CustomTable
      idKeyName='start_date'
      showFilter={true}
      columns={columns}
      data={mytimesheetList ?? []}
      handleNavigation={handleNavigation}
    >
      <div className="flex items-center justify-start  space-x-3 mb-4">
      <button
        onClick={() => {
          removeCookieData("timesheetsReport");
          handleNavigation(
            "/mytimesheetreport",
            timesheetList?.missing_timesheet && timesheetList?.missing_timesheet.length > 0
              ? moment(timesheetList?.missing_timesheet[0].start_date).format("D MMM YYYY") +
                  " - " +
                  moment(timesheetList?.missing_timesheet[0].end_date).format("D MMM YYYY")
              : "",
          );
        }}
        className='flex items-center px-4 py-2 text-sm mb-3 bg-gray-900 text-white dark:text-gray-400 rounded-lg focus:outline-none hover:bg-gray-600'
      >
        Add TimeSheet
      </button>

      <button
        onClick={() => {
          navigate("/employeeskills");
        }}
        className='flex items-center px-4 py-2 text-sm mb-3 bg-gray-900 text-white dark:text-gray-400 rounded-lg focus:outline-none hover:bg-gray-600'
      >
        Update Your Skills
      </button>
      </div>
      <div>
        <div className='max-w-4xl gap-x-6 gap-y-4 grid gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 '>
          <div>
            <label
              htmlFor='status'
              className='block text-sm font-medium leading-6 text-gray-900 dark:text-gray-400'
            >
              Status
            </label>
            <div className='mt-2'>
              <select
                id='status'
                value={statusValueData}
                onChange={(e) => {
                  setCurrentPage(1);
                  getCall(Number(e.target.value), 1);
                  setStateValueData(e.target.value);
                }}
                className='shadow-sm bg-white border border-gray-300 dark:text-gray-400 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-700  dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light'
              >
                {timesheetList?.options.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.display}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className='flex space-x-4'>
            {/* <button
            onClick={() => handleTableRefresh()}
            className='flex  items-center px-4 py-2 mt-9 text-sm bg-blue-700 hover:bg-blue-800 text-white rounded-lg focus:outline-none '
          >
            <MagnifyingGlassIcon className='h-5 w-5 text-white' />
          </button> */}
            <button
              onClick={() => refreshTable()}
              className='flex  items-center px-4 py-2 mt-9 text-sm bg-blue-700 hover:bg-blue-800 text-white rounded-lg focus:outline-none '
            >
              <ArrowPathIcon className='h-5 w-5 text-white' />
            </button>
          </div>
        </div>
        <div className='flex  items-center justify-between mt-5'>
          <span className='text-sm text-gray-700 dark:text-gray-400'>
            Showing{" "}
            <span className='font-semibold text-gray-900 dark:text-white'>
              {Number(timesheetList?.record_total_count) > 0 ? startRecord : 0}
            </span>{" "}
            to <span className='font-semibold text-gray-900 dark:text-white'>{endRecord}</span> of{" "}
            <span className='font-semibold text-gray-900 dark:text-white'>
              {timesheetList?.record_total_count ?? 0}
            </span>{" "}
            Records
          </span>
          {Number(timesheetList?.record_total_count) > 10 && (
            <div className='inline-flex xs:mt-0'>
              <button
                disabled={currentPage === 1}
                onClick={() => handlePrePage()}
                className='flex items-center justify-center px-3 h-8 text-sm font-medium text-white bg-gray-800 rounded-s hover:bg-gray-900 dark:bg-gray-900 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white'
              >
                <ArrowLeftIcon className='h-4 w-4 text-white mr-2' />
                Prev
              </button>
              <select
                id='page'
                value={currentPage}
                onChange={(e) => {
                  setCurrentPage(Number(e.target.value));
                  getCall(Number(statusValueData), Number(e.target.value));
                }}
                className='shadow-sm bg-white border border-gray-800 dark:text-gray-400 text-gray-900 text-sm  focus:ring-blue-500 focus:border-blue-500 block w-full px-2 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-700  dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light'
              >
                {Array.from({ length: totalPages }, (_, index) => (
                  <option key={index + 1} value={index + 1}>
                    {index + 1}
                  </option>
                ))}
              </select>
              <button
                disabled={currentPage === totalPages}
                onClick={() => handleNextPage()}
                className='flex items-center justify-center px-3 h-8 text-sm font-medium text-white bg-gray-800 border-0 border-s border-gray-700 rounded-e hover:bg-gray-900 dark:bg-gray-900 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white'
              >
                Next
                <ArrowRightIcon className='h-4 w-4 text-white ml-2' />
              </button>
            </div>
          )}
        </div>
      </div>
    </CustomTable>
  );
};

export default TimesheetPage;
